import type { AppState } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import { getAuthToken, logout } from "./auth";

type BoardDocument = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
};

export type Board = {
  id: string;
  workspaceId: string;
  title: string;
  document?: BoardDocument;
  revision: number;
  createdAt: string;
  updatedAt: string;
};
export type LoadedBoard = Board & {
  document: BoardDocument;
};

export class BoardLimitError extends Error {
  constructor() {
    super(
      "O plano Basic permite salvar apenas 1 painel. Faça upgrade para o Premium para liberar painéis ilimitados.",
    );
    this.name = "BoardLimitError";
  }
}

const API_URL =
  import.meta.env.VITE_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:3004`;

const request = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {}),
      ...options?.headers,
    },
  });
  if (!response.ok) {
    if (response.status === 401) {
      logout();
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    const body = await response.json().catch(() => null);
    if (body?.error === "board_limit_reached") {
      throw new BoardLimitError();
    }
    throw new Error(body?.message || `Board API returned ${response.status}`);
  }
  return response.status === 204 ? (undefined as T) : response.json();
};

export const loadBoard = async (id: string): Promise<LoadedBoard> => {
  const response = await request<{ board: Board }>(`/api/boards/${id}`);
  return response.board as LoadedBoard;
};

export const listBoards = async () => {
  const response = await request<{ boards: Board[] }>("/api/boards");
  return response.boards;
};

export const createBoard = async (input: {
  title: string;
  document: BoardDocument;
}) => {
  const response = await request<{ board: Board }>("/api/boards", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.board;
};

export const saveBoard = async (id: string, document: BoardDocument) => {
  const response = await request<{ board: Board }>(`/api/boards/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ document }),
  });
  return response.board;
};

export const renameBoard = async (id: string, title: string) => {
  const response = await request<{ board: Board }>(`/api/boards/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
  return response.board;
};

export const deleteBoard = async (id: string) => {
  await request<void>(`/api/boards/${id}`, { method: "DELETE" });
};
